// Telas de entrada: vinheta de abertura, login/cadastro e redefinição de senha.
// Extraído de App.js sem alteração de comportamento.
import { useState, useEffect, useRef } from "react";
import React from "react";
import { loadLS } from "../lib/storage";
import { sb } from "../lib/supabase";
import { ACCENTS, hexToRgb, useIsMobile } from "../lib/theme";
import { GlockIcon } from "../components/icons";
import { CONTEST_LIST, LEGAL } from "../lib/constants";
import { checkNewPassword } from "../lib/account";
import { authPalette, AuthBackground } from "../components/authUi";

// ── BOOT SEQUENCE — vinheta de abertura ("armando o sistema") ────────────────
const BOOT_STEPS = [
  "VERIFICANDO CREDENCIAIS",
  "CARREGANDO CICLO DE ESTUDOS",
  "SINCRONIZANDO ROTINA",
  "CONFERINDO SIMULADOS",
  "CALIBRANDO PAINEL",
  "SISTEMA ARMADO",
];

export function BootSequence({ onDone }) {
  const P = authPalette();
  const accentId = loadLS("sf_accentId", "blue");
  const accentObj = ACCENTS.find(a => a.id === accentId);
  const ac = P.themed ? P.ac : (accentObj ? accentObj.hex : P.ac);   // tema ativo manda na cor
  const [rv, gv, bv] = hexToRgb(ac);

  const reduced = typeof window !== "undefined" && window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const TOTAL = reduced ? 900 : 2500;
  const stepGap = TOTAL / (BOOT_STEPS.length + 1);

  const [pct, setPct] = useState(0);
  const [flash, setFlash] = useState(false);
  const [out, setOut] = useState(false);
  const doneRef = useRef(false);

  function finish() {
    if (doneRef.current) return;
    doneRef.current = true;
    setOut(true);
    setTimeout(onDone, 450);
  }

  useEffect(() => {
    // Evita o "flash branco" ao final da animação: o boot é um overlay fixo
    // que faz fade de opacidade ao terminar, revelando o fundo do <body>
    // (branco por padrão). Deixando o body/html escuros desde já, o fade
    // revela escuro suavemente em vez de branco.
    document.body.style.backgroundColor = P.bodyBg;
    document.documentElement.style.backgroundColor = P.bodyBg;

    const start = Date.now();
    const iv = setInterval(() => {
      const p = Math.min(100, Math.round(((Date.now() - start) / TOTAL) * 100));
      setPct(p);
      if (p >= 100) { clearInterval(iv); finish(); }
    }, 30);
    const flashTimer = setTimeout(() => setFlash(true), Math.max(TOTAL - (reduced ? 300 : 500), 0));
    function onKey(e) { if (e.key === "Enter" || e.key === "Escape" || e.key === " ") finish(); }
    window.addEventListener("keydown", onKey);
    return () => { clearInterval(iv); clearTimeout(flashTimer); window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const N_TICKS = 20;
  const GLOCK_D = "m 93.183329,501.40912 c -12.22981,-1.14223 -12.81704,-1.99168 -13.4963,-19.52284 l -0.5037,-13 -7.33857,-0.5 c -6.42534,-0.43778 -7.62919,-0.83815 -9.67396,-3.21737 -8.56301,-9.96356 -0.48232,-47.77045 18.23016,-85.29296 3.28859,-6.59432 11.51707,-21.88967 18.28551,-33.98967 21.377231,-38.21628 39.093841,-74.22394 44.335721,-90.10896 13.15874,-39.87631 10.97911,-68.89396 -6.15774,-81.97892 -6.85488,-5.23409 -13.83597,-7.35414 -27.17901,-8.25384 -14.176511,-0.9559 -17.002111,-2.26022 -17.002111,-7.84829 0,-2.33258 1.31963,-6.01292 3.66698,-10.22694 l 3.666971,-6.58305 0.083,-33.16947 0.083,-33.16946 2.42574,-2.58206 c 3.08401,-3.28273 7.74487,-4.566403 16.60556,-4.573433 l 7.03136,-0.006 5.93739,-5.999996 c 5.45382,-5.51133 6.29314,-6 10.30525,-6 3.97756,0 4.92619,0.53614 10.61623,6 l 6.24838,5.999996 37.41507,0.003 c 53.13145,0.004 149.55983,1.911703 152.46562,3.016483 1.52114,0.57833 2.44945,1.72355 2.44945,3.02177 0,1.14977 0.46583,2.96091 1.03518,4.02475 0.99586,1.86081 2.4935,1.9343 39.41822,1.9343 21.11066,0 39.09483,-0.27314 39.96481,-0.60699 0.88371,-0.33911 1.58855,-1.77341 1.5971,-3.25 0.0316,-5.45046 0.45921,-5.58312 20.69577,-6.42027 10.33391,-0.4275 61.31392,-1.1025 113.28892,-1.500003 l 94.5,-0.72274 0.29971,-5.743416 c 0.26857,-5.14683 0.55423,-5.80731 2.75,-6.35841 4.18996,-1.05161 7.75439,0.53768 13.20764,5.88898 2.89155,2.83748 7.61655,6.325076 10.5,7.750219 3.18314,1.57326 6.10084,3.94086 7.42725,6.0269 2.5448,4.00222 3.43539,4.95158 8.8154,9.39721 l 4,3.3053 0.32585,17.36661 c 0.40395,21.52903 -0.11691,23.64354 -6.84356,27.78249 l -4.98229,3.06563 -0.006,19.25925 c -0.003,10.59258 -0.47402,20.94602 -1.04656,23.00764 -1.63797,5.89804 -5.00622,7.10321 -19.44786,6.95849 -22.80691,-0.22854 -34.30375,-1.26468 -36.92681,-3.32798 -1.99675,-1.57097 -4.13084,-1.87891 -13.02099,-1.87891 -9.07025,0 -11.03095,0.29439 -13.34239,2.00331 -2.52284,1.86522 -5.2258,2.07941 -39.20951,3.10713 -20.07493,0.6071 -48.52675,0.8104 -63.22625,0.45178 -31.01483,-0.75666 -34.13696,-0.33024 -36.34927,4.96457 -1.91541,4.58422 -2.03935,70.74258 -0.13892,74.1525 0.66801,1.19859 3.12706,3.56391 5.46456,5.25626 5.44095,3.93924 5.62982,7.24922 0.56574,9.91475 -1.5868,0.83522 -21.76823,1.1497 -73.78212,1.1497 -80.33705,0 -75.59949,0.42844 -82.98736,-7.5049 -3.12491,-3.35563 -4.92879,-4.49415 -7.11615,-4.49136 -3.55304,0.005 -8.7438,3.01919 -10.92165,6.34301 -3.92196,5.98566 -77.50846,187.89759 -77.50846,191.60733 0,1.19628 0.65487,2.04592 1.57692,2.04592 0.86731,0 2.89231,1.31538 4.5,2.92308 2.86033,2.86033 2.92308,3.14892 2.92308,13.44423 0,9.47139 -0.2245,10.78139 -2.25,13.12928 -1.2436,1.44153 -4.03916,3.02132 -6.25,3.53191 -4.47436,1.03334 -124.92907,1.02833 -136.000001,-0.006 z M 443.0311,260.78346 c 5.94219,-2.02471 10.28882,-6.33386 11.70901,-11.60805 0.60808,-2.25821 1.17002,-12.24709 1.24877,-22.19749 0.12156,-15.35959 -0.12442,-18.69363 -1.62891,-22.07799 -4.80256,-10.80343 -8.69874,-12.01365 -38.67664,-12.01365 h -23.5 v 11.73756 11.73755 l 12.25,12.27689 c 6.7375,6.75228 12.25,12.92848 12.25,13.72488 0,0.79639 -1.18273,2.39107 -2.62828,3.54372 -2.38675,1.90313 -2.50162,2.29995 -1.25,4.31808 2.00163,3.22746 -0.25511,4.37384 -7.47742,3.79838 -9.10356,-0.72534 -16.9433,-5.13439 -26.01729,-14.63204 -4.20638,-4.40276 -8.28144,-8.00502 -9.05569,-8.00502 -3.45944,0 -4.57132,-1.25816 -4.57132,-5.17273 0,-2.17731 -0.42863,-4.75965 -0.9525,-5.73852 -1.61186,-3.01178 -11.40394,-9.10001 -17.01806,-10.58098 -7.06056,-1.86255 -7.68188,-2.61308 -5.67108,-6.85054 2.1303,-4.48925 2.18477,-9.65723 0.1018,-9.65723 -2.58747,0 -9.15264,3.7936 -11.3087,6.53458 -3.89026,4.94566 -4.62311,9.20686 -4.6376,26.96542 -0.01,11.70286 0.42549,18.51351 1.39613,21.85725 1.61089,5.54936 6.78084,11.19488 11.50908,12.56779 1.69451,0.49203 24.71327,0.93518 51.15279,0.98478 41.63916,0.0781 48.70133,-0.12429 52.77591,-1.51264 z";

  return (
    <div onClick={finish} style={{
      position: "fixed", inset: 0, zIndex: 99999, cursor: "pointer", overflow: "hidden", userSelect: "none",
      background: P.themed ? P.pageBg : "radial-gradient(ellipse 90% 70% at 50% 40%, #08152a 0%, #030810 62%, #010306 100%)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      opacity: out ? 0 : 1, transform: out ? "scale(1.045)" : "scale(1)",
      transition: "opacity 0.45s ease, transform 0.45s ease", pointerEvents: out ? "none" : "auto",
    }}>
      <style>{`
        @keyframes bootGridIn { from{opacity:0} to{opacity:1} }
        @keyframes bootBracketIn { from{opacity:0;transform:scale(0.85)} to{opacity:1;transform:scale(1)} }
        @keyframes bootScan { 0%{transform:translateY(-100%)} 100%{transform:translateY(100%)} }
        @keyframes bootDraw { to{stroke-dashoffset:0} }
        @keyframes bootFillIn { from{opacity:0} to{opacity:0.92} }
        @keyframes bootFlash { 0%{opacity:0} 15%{opacity:1} 100%{opacity:0} }
        @keyframes bootLineIn { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:translateX(0)} }
        .boot-grid { position:absolute; inset:0; opacity:0; animation:bootGridIn 0.7s ease forwards;
          background-image:
            linear-gradient(rgba(${rv},${gv},${bv},0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(${rv},${gv},${bv},0.06) 1px, transparent 1px);
          background-size:40px 40px; }
        .boot-scan { position:absolute; left:0; right:0; height:140px;
          background:linear-gradient(180deg, transparent, rgba(${rv},${gv},${bv},0.10), transparent);
          animation:bootScan 2.8s linear infinite; }
        .boot-bracket { position:absolute; width:30px; height:30px; opacity:0; animation:bootBracketIn 0.45s ease 0.15s forwards; }
        .boot-line { display:flex; align-items:center; gap:10px; opacity:0; animation:bootLineIn 0.35s ease forwards;
          font-family:'JetBrains Mono',monospace; font-size:0.72rem; letter-spacing:0.03em; color:${P.dim}; }
        .boot-ok { opacity:0; margin-left:auto; font-weight:600; animation:bootLineIn 0.25s ease forwards; color:${ac}; }
      `}</style>

      <div className="boot-grid" />
      <div className="boot-scan" />
      <div className="boot-bracket" style={{ top: 30, left: 30, borderTop: `2px solid ${ac}`, borderLeft: `2px solid ${ac}` }} />
      <div className="boot-bracket" style={{ top: 30, right: 30, borderTop: `2px solid ${ac}`, borderRight: `2px solid ${ac}` }} />
      <div className="boot-bracket" style={{ bottom: 30, left: 30, borderBottom: `2px solid ${ac}`, borderLeft: `2px solid ${ac}` }} />
      <div className="boot-bracket" style={{ bottom: 30, right: 30, borderBottom: `2px solid ${ac}`, borderRight: `2px solid ${ac}` }} />

      {!reduced && (
        <div style={{
          position: "absolute", width: 560, height: 560, borderRadius: "50%", pointerEvents: "none",
          background: `radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(251,191,36,0.4) 22%, rgba(${rv},${gv},${bv},0.3) 45%, transparent 72%)`,
          opacity: 0,
          animation: flash ? "bootFlash 0.5s ease forwards" : "none",
        }} />
      )}

      <div style={{ position: "relative", width: 210, marginBottom: 22 }}>
        <svg viewBox="0 0 767 554" width="210" style={{ display: "block", filter: `drop-shadow(0 0 20px rgba(${rv},${gv},${bv},0.55))` }}>
          <path d={GLOCK_D} fill="none" stroke={ac} strokeWidth="7" pathLength="1"
            style={{
              strokeDasharray: 1, strokeDashoffset: 1,
              animation: reduced ? "bootDraw 0.5s ease 0.1s forwards" : "bootDraw 1.5s cubic-bezier(.6,0,.4,1) 0.25s forwards"
            }} />
          <path d={GLOCK_D} fill={ac}
            style={{ opacity: 0, animation: `bootFillIn 0.4s ease ${reduced ? 0.6 : 1.75}s forwards` }} />
        </svg>
      </div>

      <div style={{ textAlign: "center", marginBottom: 26 }}>
        <div style={{ fontFamily: P.fontDisplay, fontWeight: 900, fontSize: "2.15rem", letterSpacing: "0.05em", textTransform: "uppercase", color: P.text, lineHeight: 1 }}>
          G17 App
        </div>
        <div style={{ fontFamily: P.fontBody, fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", color: ac, marginTop: "0.5rem", fontWeight: 600 }}>
          O arsenal do concurseiro policial
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 270, marginBottom: 22 }}>
        {BOOT_STEPS.map((label, i) => (
          <div key={label} className="boot-line" style={{ animationDelay: `${(0.35 + i * (stepGap / 1000)).toFixed(2)}s` }}>
            <span style={{ color: ac, width: 14, flexShrink: 0 }}>{"\u203A"}</span>
            <span>{label}</span>
            <span className="boot-ok" style={{ animationDelay: `${(0.35 + i * (stepGap / 1000) + (stepGap / 1000) * 0.55).toFixed(2)}s` }}>{"\u2713"}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
        {Array.from({ length: N_TICKS }).map((_, i) => (
          <div key={i} style={{
            width: 8, height: 16, borderRadius: 1,
            background: pct >= (i + 1) * (100 / N_TICKS) ? ac : P.tickOff,
            boxShadow: pct >= (i + 1) * (100 / N_TICKS) ? `0 0 6px rgba(${rv},${gv},${bv},0.6)` : "none",
            transition: "background 0.15s, box-shadow 0.15s"
          }} />
        ))}
      </div>

      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem", color: P.faint, letterSpacing: "0.08em", marginBottom: 30 }}>
        {String(pct).padStart(3, "0")}% — G17 APP
      </div>

      <div style={{
        position: "absolute", bottom: 24, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
        color: P.faint, letterSpacing: "0.06em", opacity: pct > 30 ? 0.75 : 0, transition: "opacity 0.4s"
      }}>
        toque, clique ou ESC para pular
      </div>
    </div>
  );
}

export function AuthScreen({ onAuth }) {
  const P = authPalette();
  const isMobile = useIsMobile();
  const [mode,     setMode]     = useState("login");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [name,     setName]     = useState("");
  const [contest,  setContest]  = useState("");
  const [customC,  setCustomC]  = useState("");
  const [customEmoji, setCustomEmoji] = useState("⭐");
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState("");
  const [ok,       setOk]       = useState("");
  const [mounted,  setMounted]  = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [agree,    setAgree]    = useState(false); // aceite dos Termos e da Política (LGPD)
  React.useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

  // Usa apenas os concursos do CONTEST_LIST (perfil) para manter consistência
  const allC = [
    ...CONTEST_LIST.filter(c => c.id !== "outros").map(c => ({ id:c.id, label:c.label, full:c.full, color:c.color })),
    { id:"outro", label:"Personalizar", full:"Concurso personalizado", color:"#94a3b8" }
  ];

  async function handleSubmit() {
    setErr(""); setOk("");
    if (!email || !password) { setErr("Preencha e-mail e senha."); return; }
    if (mode === "register" && !name.trim()) { setErr("Informe seu nome."); return; }
    if (mode === "register" && !agree) { setErr(`Para criar a conta, aceite os Termos de Uso e a Política de Privacidade e confirme ter ${LEGAL.minAge} anos ou mais.`); return; }
    if (mode === "register") {
      setLoading(true);
      const pwErr = await checkNewPassword(password);
      setLoading(false);
      if (pwErr) { setErr(pwErr); return; }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        const { data, error } = await sb.auth.signInWithPassword({ email, password });
        if (error) {
          const msgs = {
            "Invalid login credentials": "Email ou senha incorretos.",
            "Email not confirmed": "Confirme seu e-mail antes de entrar.",
            "User already registered": "Este e-mail já está cadastrado.",
            "Password should be at least 6 characters": "A senha deve ter pelo menos 6 caracteres.",
          };
          setErr(msgs[error.message] || error.message);
          setLoading(false); return;
        }
        // Block login if email not confirmed
        if (!data.user.email_confirmed_at && data.user.identities?.length > 0) {
          await sb.auth.signOut({ scope: "local" });
          setErr("Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.");
          setLoading(false); return;
        }
        // Conta removida pelo admin: só dá para conferir o perfil quando não há
        // código de 2 etapas pendente (antes do código o banco não entrega dados).
        // Com 2 etapas, a conferência acontece depois, no monitor de perfil do app.
        let mfaPending = false;
        try { const { data: al } = await sb.auth.mfa.getAuthenticatorAssuranceLevel(); mfaPending = !!al && al.nextLevel === "aal2" && al.currentLevel !== "aal2"; } catch {}
        if (!mfaPending) {
          const { data: profileCheck, error: pErr } = await sb.from("profiles").select("id").eq("id", data.user.id).maybeSingle();
          if (!pErr && !profileCheck) {
            await sb.auth.signOut({ scope: "local" });
            setErr("Esta conta não tem mais cadastro no G17 (pode ter sido removida). Crie uma conta nova ou fale com o suporte.");
            setLoading(false); return;
          }
        }
        onAuth(data.user);
      } else {
        // "outros" no perfil usa nome personalizado com estrela
        const targetC = contest === "outro"
          ? (customC.trim() ? customEmoji + " " + customC.trim() : "")
          : contest;
        const { data, error } = await sb.auth.signUp({
          email, password,
          options: { data: { public_name: name.trim(), target_contest: targetC, terms_version: LEGAL.version, terms_accepted_at: new Date().toISOString() } }
        });
        if (error) {
          const msgs = {
            "User already registered": "Este e-mail já está cadastrado.",
            "Password should be at least 6 characters": "A senha deve ter pelo menos 6 caracteres.",
          };
          setErr(msgs[error.message] || error.message);
          setLoading(false); return;
        }
        if (data.user) {
          setOk("Conta criada! Verifique seu e-mail para confirmar e depois faça login.");
          setMode("login");
        }
      }
    } catch(e) { setErr(String(e)); }
    setLoading(false);
  }

  async function handleForgot() {
    setErr(""); setOk("");
    if (!email) { setErr("Informe seu e-mail."); return; }
    setLoading(true);
    try {
      // Resposta neutra de propósito: não revela se o e-mail tem conta no G17
      // (evita que alguém descubra quem está cadastrado testando e-mails).
      // Só envia o e-mail se a conta existe no G17 (perfil). A mensagem ao usuário é igual nos dois casos.
      let canReset = true;
      try { const { data: ok } = await sb.rpc("can_reset_password", { p_email: email.trim() }); canReset = ok !== false; } catch {}
      if (!canReset) {
        setOk("Se esse e-mail estiver cadastrado, você receberá um link para redefinir a senha em instantes. Confira também a caixa de spam.");
        setLoading(false); return;
      }
      const { error } = await sb.auth.resetPasswordForEmail(email.trim(), { redirectTo: "https://g17app.com.br/#type=recovery" });
      if (error && (error.status === 429 || /rate limit|too many/i.test(error.message || ""))) {
        setErr("Muitas tentativas. Aguarde alguns minutos e tente novamente.");
      } else {
        if (error) console.warn("resetPasswordForEmail:", error.message);
        setOk("Se esse e-mail estiver cadastrado, você receberá um link para redefinir a senha em instantes. Confira também a caixa de spam.");
      }
    } catch(e) { setErr("Não foi possível enviar agora. Tente novamente em instantes."); }
    setLoading(false);
  }

  const cardStyle = {
    position: "relative",
    width: "100%",
    maxWidth: 440,
    background: P.cardBg,
    border: `1px solid rgba(${P.rgb},0.18)`,
    borderRadius: 20,
    padding: isMobile ? "1.8rem 1.4rem 1.6rem" : "2.8rem 2.8rem 2.4rem",
    boxShadow: `0 0 0 1px rgba(${P.rgb},0.06), 0 32px 80px rgba(0,0,0,0.7), 0 0 60px rgba(${P.rgb},0.05)`,
    backdropFilter: "blur(24px)",
    transition: "opacity 0.5s ease, transform 0.5s ease",
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(24px)",
  };

  const inputStyle = {
    width: "100%",
    background: P.inputBg,
    border: `1px solid rgba(${P.rgb},0.2)`,
    borderRadius: 10,
    padding: "0.85rem 1.1rem",
    color: P.text,
    fontSize: "0.9rem",
    outline: "none",
    boxSizing: "border-box",
    marginBottom: "0.75rem",
    fontFamily: P.fontBody,
    transition: "border-color 0.2s",
  };

  const btnStyle = {
    width: "100%",
    background: P.btnBg,
    color: P.onAc,
    border: "none",
    borderRadius: 11,
    padding: "0.9rem",
    fontWeight: 800,
    fontSize: "0.97rem",
    cursor: loading ? "wait" : "pointer",
    marginBottom: "1.2rem",
    opacity: loading ? 0.7 : 1,
    letterSpacing: "0.03em",
    boxShadow: `0 4px 24px rgba(${P.rgb},0.35)`,
    fontFamily: P.fontBody,
    transition: "transform 0.15s, box-shadow 0.15s",
  };

  const glockRef = React.useRef(null);
  const glockAnimRef = React.useRef({ tiltX: 0, tiltY: 0, scale: 1, glowIntensity: 0, particles: [], hovered: false });
  const glockRafRef = React.useRef(null);

  const titleRef = React.useRef(null);
  const titleAnimRef = React.useRef({ tiltX: 0, tiltY: 0, scale: 1, particles: [], hovered: false });
  const titleRafRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (glockRafRef.current) cancelAnimationFrame(glockRafRef.current);
      if (titleRafRef.current) cancelAnimationFrame(titleRafRef.current);
    };
  }, []);

  function spawnParticles() {
    const state = glockAnimRef.current;
    const count = 18;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const speed = 1.8 + Math.random() * 2.8;
      state.particles.push({
        x: 0, y: 0,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        size: 2 + Math.random() * 3,
      });
    }
  }

  function onGlockMouseEnter(e) {
    const state = glockAnimRef.current;
    if (!state.hovered) { state.hovered = true; spawnParticles(); }
    animateGlock();
  }

  function onGlockMouseLeave() {
    glockAnimRef.current.hovered = false;
  }

  function onGlockMouseMove(e) {
    const el = glockRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    glockAnimRef.current.targetTiltX = dy * 22;
    glockAnimRef.current.targetTiltY = -dx * 22;
  }

  function animateGlock() {
    const state = glockAnimRef.current;
    const el = glockRef.current;
    if (!el) return;

    // Lerp tilt
    state.tiltX = (state.tiltX || 0) + ((state.targetTiltX || 0) - (state.tiltX || 0)) * 0.12;
    state.tiltY = (state.tiltY || 0) + ((state.targetTiltY || 0) - (state.tiltY || 0)) * 0.12;

    // Lerp scale & glow
    const targetScale = state.hovered ? 1.12 : 1;
    const targetGlow  = state.hovered ? 1 : 0;
    state.scale        = (state.scale || 1) + (targetScale - (state.scale || 1)) * 0.1;
    state.glowIntensity = (state.glowIntensity || 0) + (targetGlow - (state.glowIntensity || 0)) * 0.1;

    if (!state.hovered) {
      state.targetTiltX = 0;
      state.targetTiltY = 0;
    }

    // Update particles
    state.particles = state.particles.filter(p => p.life > 0.01);
    state.particles.forEach(p => {
      p.x += p.vx; p.y += p.vy;
      p.vx *= 0.94; p.vy *= 0.94;
      p.life *= 0.92;
    });

    const gi = state.glowIntensity;
    const glowA = 0.5 + gi * 0.5;
    const glowB = 0.2 + gi * 0.4;
    const glowSize1 = 18 + gi * 28;
    const glowSize2 = 40 + gi * 60;

    el.style.transform = `perspective(400px) rotateX(${state.tiltX}deg) rotateY(${state.tiltY}deg) scale(${state.scale}) rotate(-8deg)`;
    el.style.filter = `drop-shadow(0 0 ${glowSize1}px rgba(${P.rgb},${glowA})) drop-shadow(0 0 ${glowSize2}px rgba(${P.rgb},${glowB}))`;

    // Render particles into overlay canvas
    const cvs = el.querySelector("canvas.glock-particles");
    if (cvs) {
      const ctx = cvs.getContext("2d");
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      const cx = cvs.width / 2, cy = cvs.height / 2;
      state.particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(cx + p.x * 4, cy + p.y * 4, p.size * p.life, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${P.rgbLt},${p.life * 0.9})`;
        ctx.fill();
        // rastro
        ctx.beginPath();
        ctx.arc(cx + p.x * 4 - p.vx * 3, cy + p.y * 4 - p.vy * 3, p.size * p.life * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${P.rgb},${p.life * 0.4})`;
        ctx.fill();
      });
    }

    const stillAnimating = state.hovered
      || Math.abs(state.tiltX) > 0.1 || Math.abs(state.tiltY) > 0.1
      || Math.abs(state.scale - 1) > 0.002 || state.glowIntensity > 0.01
      || state.particles.length > 0;

    if (stillAnimating) {
      glockRafRef.current = requestAnimationFrame(animateGlock);
    } else {
      el.style.transform = "";
      el.style.filter = "";
    }
  }

  function spawnTitleParticles() {
    const state = titleAnimRef.current;
    const count = 14;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
      const speed = 1.2 + Math.random() * 2.0;
      state.particles.push({
        x: 0, y: 0,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        size: 1.5 + Math.random() * 2.5,
      });
    }
  }

  function onTitleMouseEnter(e) {
    const state = titleAnimRef.current;
    if (!state.hovered) { state.hovered = true; spawnTitleParticles(); }
    animateTitle();
  }
  function onTitleMouseLeave() { titleAnimRef.current.hovered = false; }
  function onTitleMouseMove(e) {
    const el = titleRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    titleAnimRef.current.targetTiltX = dy * 14;
    titleAnimRef.current.targetTiltY = -dx * 14;
  }

  function animateTitle() {
    const state = titleAnimRef.current;
    const el = titleRef.current;
    if (!el) return;

    state.tiltX = (state.tiltX || 0) + ((state.targetTiltX || 0) - (state.tiltX || 0)) * 0.1;
    state.tiltY = (state.tiltY || 0) + ((state.targetTiltY || 0) - (state.tiltY || 0)) * 0.1;
    if (!state.hovered) { state.targetTiltX = 0; state.targetTiltY = 0; }

    const targetScale = state.hovered ? 1.06 : 1;
    state.scale = (state.scale || 1) + (targetScale - (state.scale || 1)) * 0.1;

    state.particles = state.particles.filter(p => p.life > 0.01);
    state.particles.forEach(p => {
      p.x += p.vx; p.y += p.vy;
      p.vx *= 0.93; p.vy *= 0.93;
      p.life *= 0.91;
    });

    el.style.transform = `perspective(600px) rotateX(${state.tiltX}deg) rotateY(${state.tiltY}deg) scale(${state.scale})`;

    // Render particles into overlay canvas
    const cvs = el.querySelector("canvas.title-particles");
    if (cvs) {
      const ctx = cvs.getContext("2d");
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      const cx = cvs.width / 2, cy = cvs.height / 2 + 10;
      state.particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(cx + p.x * 5, cy + p.y * 5, p.size * p.life, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${P.rgbLt},${p.life * 0.8})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx + p.x * 5 - p.vx * 3, cy + p.y * 5 - p.vy * 3, p.size * p.life * 0.35, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${P.rgb},${p.life * 0.35})`;
        ctx.fill();
      });
    }

    const stillAnimating = state.hovered
      || Math.abs(state.tiltX) > 0.05 || Math.abs(state.tiltY) > 0.05
      || Math.abs(state.scale - 1) > 0.001 || state.particles.length > 0;

    if (stillAnimating) {
      titleRafRef.current = requestAnimationFrame(animateTitle);
    } else {
      el.style.transform = "";
    }
  }

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes glow-pulse {
          0%, 100% { filter: drop-shadow(0 0 18px rgba(${P.rgb},0.5)) drop-shadow(0 0 40px rgba(${P.rgb},0.2)); }
          50%       { filter: drop-shadow(0 0 28px rgba(${P.rgb},0.8)) drop-shadow(0 0 60px rgba(${P.rgb},0.35)); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(-8deg); }
          50%       { transform: translateY(-10px) rotate(-8deg); }
        }
        @keyframes scan {
          0%   { transform: translateY(-100%); opacity: 0; }
          10%  { opacity: 0.6; }
          90%  { opacity: 0.6; }
          100% { transform: translateY(400%); opacity: 0; }
        }
        .auth-input:focus { border-color: rgba(${P.rgb},0.6) !important; background: rgba(${P.rgb},0.06) !important; }
        .auth-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 32px rgba(${P.rgb},0.5) !important; }
        .auth-btn:active:not(:disabled) { transform: translateY(0); }
        .contest-btn:hover { border-color: rgba(${P.rgb},0.5) !important; }
        .mode-link:hover { color: ${P.acLight} !important; }
        .eye-btn:hover { color: ${P.acLight} !important; }
        .glock-wrapper { cursor: crosshair; }
        .glock-wrapper:not(:hover) .glock-icon-el { animation: float 4s ease-in-out infinite, glow-pulse 3s ease-in-out infinite; }
        .g17-title { transition: color 0.3s, text-shadow 0.3s; }
      `}</style>

      {/* Fundo animado — mais escuro */}
      <div style={{ position:"fixed", inset:0, background:P.pageBg, zIndex:0 }} />
      <div style={{ position:"fixed", inset:0, backgroundImage:P.pageBg2, zIndex:0 }} />
      <AuthBackground />

      {/* Layout */}
      <div style={{ minHeight:"100dvh", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:P.fontLayout, padding:isMobile?"1.2rem 1rem 2rem":"2rem", position:"relative", zIndex:1, boxSizing:"border-box" }}>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", width:"100%", gap: isMobile?"1.2rem":"2.2rem" }}>

          {/* Glock + título */}
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", transition:"opacity 0.6s ease, transform 0.6s ease", opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(-20px)" }}>

            {/* Wrapper interativo da Glock */}
            <div className="glock-wrapper" style={{ display:"inline-block", position:"relative", padding:"1rem" }}
              onMouseEnter={onGlockMouseEnter}
              onMouseLeave={onGlockMouseLeave}
              onMouseMove={onGlockMouseMove}>

              {/* Canvas de partículas */}
              <canvas className="glock-particles" width={220} height={220}
                style={{ position:"absolute", inset:0, margin:"auto", top:"-50%", left:"-50%", right:"-50%", bottom:"-50%", width:220, height:220, pointerEvents:"none", zIndex:2 }} />

              {/* Anel de energia no hover */}
              <div style={{
                position:"absolute", inset:-16, borderRadius:"50%",
                border:`1px solid rgba(${P.rgb},0.0)`,
                boxShadow:`0 0 0 0 rgba(${P.rgb},0)`,
                transition:"all 0.4s ease",
                pointerEvents:"none", zIndex:1,
              }} />

              <div ref={glockRef} className="glock-icon-el"
                style={{ display:"inline-block", transformOrigin:"center center", willChange:"transform, filter", position:"relative", zIndex:3 }}>
                <GlockIcon size={isMobile?64:92} color={P.ac} />
              </div>
            </div>

            <div style={{ marginTop:"1rem", position:"relative", display:"inline-block", cursor:"crosshair" }}
              ref={titleRef}
              onMouseEnter={onTitleMouseEnter}
              onMouseLeave={onTitleMouseLeave}
              onMouseMove={onTitleMouseMove}>
              {/* Canvas de partículas do título */}
              <canvas className="title-particles" width={300} height={120}
                style={{ position:"absolute", top:"-40%", left:"-20%", width:300, height:120, pointerEvents:"none", zIndex:2 }} />
              <div style={{ fontFamily:P.fontDisplay, fontWeight:900, fontSize:isMobile?"2.1rem":"2.6rem", color:P.text, letterSpacing:"0.04em", lineHeight:1, textTransform:"uppercase", position:"relative", zIndex:1, transition:"text-shadow 0.3s", textShadow: "0 0 0px transparent" }} className="g17-title">G17 APP</div>
              <div style={{ fontSize:"0.72rem", color:P.ac2, letterSpacing:"0.22em", textTransform:"uppercase", marginTop:"0.5rem", fontWeight:600, fontFamily:P.fontInter, position:"relative", zIndex:1 }}>O arsenal do concurseiro policial</div>
            </div>
          </div>

          {/* Card */}
          <div style={cardStyle}>
            {/* Linha de scan animada */}
            <div style={{ position:"absolute", inset:0, borderRadius:20, overflow:"hidden", pointerEvents:"none" }}>
              <div style={{ position:"absolute", left:0, right:0, height:1, background:`linear-gradient(90deg, transparent, rgba(${P.rgb},0.4), transparent)`, animation:"scan 4s ease-in-out infinite 1s" }} />
            </div>

            <h2 style={{ margin:"0 0 1.8rem", fontFamily:P.fontInter, fontSize:"1.3rem", fontWeight:700, color:P.text, letterSpacing:"-0.01em" }}>
              {mode === "login" ? "Bem-vindo de volta 👋" : mode === "forgot" ? "Recuperar sua conta" : "Criar sua conta"}
            </h2>

            {err && (
              <div style={{ color:P.errText, fontSize:"0.82rem", marginBottom:"1rem", background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:8, padding:"0.6rem 0.9rem" }}>
                {err}
              </div>
            )}
            {ok && (
              <div style={{ color:P.okText, fontSize:"0.82rem", marginBottom:"1rem", background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.2)", borderRadius:8, padding:"0.6rem 0.9rem" }}>
                {ok}
              </div>
            )}

            {mode === "register" && (
              <>
                <input className="auth-input" type="text" value={name} onChange={e=>setName(e.target.value)}
                  placeholder="Seu nome (aparece no ranking)" style={inputStyle} />
                <div style={{ marginBottom:"0.75rem" }}>
                  <label style={{ fontSize:"0.68rem", color:P.ac2, display:"block", marginBottom:"0.5rem", textTransform:"uppercase", letterSpacing:"0.12em", fontWeight:700 }}>Concurso almejado</label>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"0.35rem", marginBottom: contest==="outro" ? "0.5rem" : 0 }}>
                    {allC.map(c => (
                      <button key={c.id} className="contest-btn" onClick={()=>setContest(c.id)}
                        style={{ background:contest===c.id?c.color+"25":P.chipBg, border:`1.5px solid ${contest===c.id?c.color:`rgba(${P.rgb},0.15)`}`, borderRadius:8, padding:"0.35rem 0.75rem", color:contest===c.id?c.color:P.dim, fontSize:"0.78rem", fontWeight:contest===c.id?700:400, cursor:"pointer", transition:"all 0.15s" }}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                  {contest === "outro" && (
                    <div style={{ marginTop:"0.5rem", display:"flex", flexDirection:"column", gap:"0.5rem" }}>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:"0.28rem" }}>
                        {["⭐","🎯","🏆","🔫","🚔","🛡️","🔍","👮","🎖️","🔥","⚡","🏅"].map(em => (
                          <button key={em} type="button" onClick={()=>setCustomEmoji(em)}
                            style={{ width:36, height:36, fontSize:"1.1rem", background:customEmoji===em?`rgba(${P.rgb},0.2)`:P.chipBg, border:`1.5px solid ${customEmoji===em?P.ac:`rgba(${P.rgb},0.15)`}`, borderRadius:7, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}>
                            {em}
                          </button>
                        ))}
                      </div>
                      <input className="auth-input" value={customC} onChange={e=>setCustomC(e.target.value)} placeholder="Nome do concurso (ex: PCSP, DELTA...)"
                        style={{ ...inputStyle, marginBottom:0 }}/>
                      {customC.trim() && (
                        <div style={{ display:"flex", alignItems:"center", gap:"0.4rem", padding:"0.4rem 0.6rem", background:`rgba(${P.rgb},0.06)`, border:`1px solid rgba(${P.rgb},0.15)`, borderRadius:7 }}>
                          <span style={{fontSize:"1rem"}}>{customEmoji}</span>
                          <span style={{fontSize:"0.82rem", fontWeight:700, color:P.ac}}>{customC.trim()}</span>
                          <span style={{fontSize:"0.65rem", color:P.faint, marginLeft:"auto"}}>prévia</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {mode === "forgot" ? (
              <>
                <p style={{ fontSize:"0.83rem", color:P.dim, margin:"0 0 1rem", lineHeight:1.6 }}>
                  Informe seu e-mail e enviaremos um link para redefinir sua senha.
                </p>
                <input className="auth-input" type="email" value={email} onChange={e=>setEmail(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&handleForgot()}
                  placeholder="Seu e-mail cadastrado" style={inputStyle} />
                <button className="auth-btn" onClick={handleForgot} disabled={loading} style={btnStyle}>
                  {loading ? "Enviando…" : "Enviar link de recuperação"}
                </button>
                <p style={{ textAlign:"center", margin:0 }}>
                  <button className="mode-link" onClick={()=>{setMode("login");setErr("");setOk("");}}
                    style={{ background:"none", border:"none", color:P.ac, fontWeight:700, cursor:"pointer", fontSize:"0.83rem", transition:"color 0.15s" }}>
                    ← Voltar ao login
                  </button>
                </p>
              </>
            ) : (
              <>
                <input className="auth-input" type="email" value={email} onChange={e=>setEmail(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                  placeholder="E-mail" style={inputStyle} />
                <div style={{ position:"relative", marginBottom:"0.75rem" }}>
                  <input className="auth-input" type={showPass ? "text" : "password"} value={password} onChange={e=>setPassword(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                    placeholder={mode === "register" ? "Senha (mínimo 8 caracteres)" : "Senha"} style={{ ...inputStyle, marginBottom:0, paddingRight:"3rem" }} />
                  <button className="eye-btn" onClick={()=>setShowPass(v=>!v)}
                    style={{ position:"absolute", right:"0.9rem", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:P.dim, cursor:"pointer", padding:0, display:"flex", alignItems:"center", transition:"color 0.15s" }}>
                    {showPass ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>

                {mode === "register" && (
                  <label style={{ display:"flex", gap:"0.6rem", alignItems:"flex-start", fontSize:"0.8rem", lineHeight:1.5, color:P.dim, cursor:"pointer", margin:"0.2rem 0 0.9rem", textAlign:"left" }}>
                    <input type="checkbox" checked={agree} onChange={e=>setAgree(e.target.checked)} style={{ width:17, height:17, marginTop:2, accentColor:P.ac, flexShrink:0 }} />
                    <span>Li e concordo com os <a href="#/termos" target="_blank" rel="noopener noreferrer" style={{ color:P.ac }}>Termos de Uso</a> e a <a href="#/privacidade" target="_blank" rel="noopener noreferrer" style={{ color:P.ac }}>Política de Privacidade</a>. Declaro ter {LEGAL.minAge} anos ou mais.</span>
                  </label>
                )}
                <button className="auth-btn" onClick={handleSubmit} disabled={loading} style={{ ...btnStyle, marginTop:"0.4rem" }}>
                  {loading
                    ? <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"0.5rem" }}>
                        <span style={{ width:14, height:14, border:`2px solid ${P.onAc}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite", display:"inline-block" }}/>
                        Aguarde…
                      </span>
                    : mode === "login" ? "Entrar" : "Criar conta"}
                </button>

                {mode === "login" && (
                  <p style={{ textAlign:"center", fontSize:"0.78rem", margin:"0 0 0.9rem" }}>
                    <button className="mode-link" onClick={()=>{setMode("forgot");setErr("");setOk("");}}
                      style={{ background:"none", border:"none", color:P.dim, cursor:"pointer", fontSize:"0.78rem", textDecoration:"underline", transition:"color 0.15s" }}>
                      Esqueceu sua senha?
                    </button>
                  </p>
                )}

                <p style={{ textAlign:"center", fontSize:"0.82rem", color:P.dim, margin:0 }}>
                  {mode === "login" ? "Não tem conta? " : "Já tem conta? "}
                  <button className="mode-link" onClick={()=>{setMode(mode==="login"?"register":"login");setErr("");setOk("");}}
                    style={{ background:"none", border:"none", color:P.ac, fontWeight:700, cursor:"pointer", fontSize:"0.82rem", transition:"color 0.15s" }}>
                    {mode === "login" ? "Criar agora" : "Fazer login"}
                  </button>
                </p>
              </>
            )}
            <p style={{ textAlign:"center", fontSize:"0.72rem", color:P.faint, margin:"1.1rem 0 0" }}>
              <a href="#/termos" style={{ color:P.dim }}>Termos de Uso</a>
              <span style={{ margin:"0 0.5rem" }}>·</span>
              <a href="#/privacidade" style={{ color:P.dim }}>Política de Privacidade</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// ── RESET PASSWORD SCREEN ────────────────────────────────────────────────────
export function ResetPasswordScreen() {
  const P = authPalette();
  const isMobile = useIsMobile();
  const [newPass,     setNewPass]     = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [msg,         setMsg]         = useState("");
  const [err,         setErr]         = useState("");
  const [loading,     setLoading]     = useState(false);
  const [sessionCheck, setSessionCheck] = useState(false);
  const [linkDead,     setLinkDead]     = useState(false);   // link usado/expirado: só resta pedir outro
  const [needCode,     setNeedCode]     = useState(false);   // conta com verificação em 2 etapas
  const [mfaCode,      setMfaCode]      = useState("");

  // Verificar se sessão está disponível
  useEffect(() => {
    const checkSession = async () => {
      // O link traz os tokens da recuperação no endereço. Usa exatamente eles,
      // para não aproveitar por engano uma sessão antiga guardada neste navegador.
      const raw = (window.location.hash || "").replace(/^#/, "").split("#").join("&");
      const hp = new URLSearchParams(raw);
      if (hp.get("error_code") || hp.get("error")) {
        setErr(hp.get("error_code") === "otp_expired"
          ? "Este link já foi usado ou expirou. Peça um novo na tela de login."
          : "Link inválido. Peça um novo na tela de login.");
        setLinkDead(true); setSessionCheck(true); return;
      }
      const at = hp.get("access_token"), rt = hp.get("refresh_token");
      if (at && rt) {
        const { error } = await sb.auth.setSession({ access_token: at, refresh_token: rt });
        if (!error) { window.history.replaceState(null, "", window.location.pathname + "#type=recovery"); setSessionCheck(true); return; }
      }
      const { data: { session }, error } = await sb.auth.getSession();
      if (error || !session) {
        setErr("Link expirado ou inválido. Solicite um novo link de recuperação.");
        setLinkDead(true); setSessionCheck(true); return;
      }
      setSessionCheck(true);
    };
    checkSession();
  }, []);

  async function handleReset() {
    if (!newPass)                    { setErr("Informe a nova senha."); return; }
    if (newPass !== confirmPass)     { setErr("As senhas não coincidem."); return; }
    setLoading(true);
    const pwErr = await checkNewPassword(newPass);
    if (pwErr) { setErr(pwErr); setLoading(false); return; }
    setErr(""); // Limpar erro anterior
    // Conta com verificação em 2 etapas: o Supabase só troca a senha depois do código.
    try {
      const { data: al } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
      if (al && al.nextLevel === "aal2" && al.currentLevel !== "aal2") {
        if (mfaCode.length !== 6) { setNeedCode(true); setErr("Digite o código de 6 dígitos do seu aplicativo autenticador."); setLoading(false); return; }
        const { data: fl } = await sb.auth.mfa.listFactors();
        const fac = fl && (fl.totp || []).find(x => x.status === "verified");
        const { error: vErr } = fac ? await sb.auth.mfa.challengeAndVerify({ factorId: fac.id, code: mfaCode }) : { error: { message: "sem fator" } };
        if (vErr) { setNeedCode(true); setMfaCode(""); setErr("Código inválido. Digite o código atual do aplicativo."); setLoading(false); return; }
      }
    } catch {}
    const { error } = await sb.auth.updateUser({ password: newPass });
    setLoading(false);
    if (error) { 
      setErr(/session/i.test(error.message || "")
        ? (setLinkDead(true), "Este link de recuperação não vale mais. Peça um novo na tela de login.")
        : /different from the old|same as the old/i.test(error.message || "") ? "A nova senha precisa ser diferente da atual."
        : error.message); 
      return; 
    }
    setMsg("Senha redefinida com sucesso! Redirecionando...");
    setTimeout(() => window.location.href = window.location.origin, 2000);
  }

  if (!sessionCheck) {
    return (
      <div style={{ minHeight:"100vh", background:P.bodyBg, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ width:40, height:40, border:`3px solid ${P.ac}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <>
      <div style={{ position:"fixed", inset:0, background:P.pageBg, zIndex:0 }} />
      <AuthBackground />
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:P.fontLayout, padding:isMobile?"1rem":"2rem", position:"relative", zIndex:1 }}>
        <div style={{ width:"100%", maxWidth:420, background:P.cardBg2, border:`1px solid rgba(${P.rgb},0.18)`, borderRadius:20, padding:isMobile?"1.8rem 1.4rem":"2.8rem", boxShadow:P.cardShadow2, backdropFilter:"blur(24px)" }}>
          <div style={{ textAlign:"center", marginBottom:"2rem" }}>
            <GlockIcon size={52} color={P.ac} />
          </div>
          <h2 style={{ color:P.text, fontFamily:P.fontInter, fontWeight:700, fontSize:"1.3rem", letterSpacing:"-0.01em", margin:"0 0 0.4rem" }}>Redefinir senha</h2>
          <p style={{ color:P.dim, fontSize:"0.83rem", margin:"0 0 1.5rem" }}>Digite sua nova senha abaixo.</p>
          {err && <div style={{ color:P.errText, fontSize:"0.82rem", marginBottom:"1rem", background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:8, padding:"0.6rem 0.9rem" }}>{err}</div>}
          {msg && <div style={{ color:P.okText, fontSize:"0.82rem", marginBottom:"1rem", background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.2)", borderRadius:8, padding:"0.6rem 0.9rem" }}>{msg}</div>}
          {["Nova senha","Confirmar senha"].map((ph,i) => (
            <input key={i} type="password"
              value={i===0?newPass:confirmPass}
              onChange={e=>i===0?setNewPass(e.target.value):setConfirmPass(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleReset()}
              placeholder={ph}
              disabled={linkDead}
              style={{ width:"100%", background:P.inputBg, border:`1px solid rgba(${P.rgb},0.2)`, borderRadius:10, padding:"0.85rem 1.1rem", color:P.text, fontSize:"0.9rem", outline:"none", boxSizing:"border-box", marginBottom:"0.75rem", fontFamily:P.fontBody }}/>
          ))}
          {needCode && !linkDead && (
            <input value={mfaCode} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="Código do autenticador (6 dígitos)"
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={e=>e.key==="Enter"&&handleReset()}
              style={{ width:"100%", background:P.inputBg, border:`1px solid rgba(${P.rgb},0.45)`, borderRadius:10, padding:"0.85rem 1.1rem", color:P.text, fontSize:"1rem", letterSpacing:"0.2em", textAlign:"center", outline:"none", boxSizing:"border-box", marginBottom:"0.75rem", fontFamily:P.fontBody }}/>
          )}
          <button onClick={handleReset} disabled={loading || linkDead}
            style={{ width:"100%", background:P.btnBg, color:P.onAc, border:"none", borderRadius:11, padding:"0.9rem", fontWeight:800, fontSize:"0.97rem", cursor:"pointer", opacity:loading?0.7:1, marginBottom:"0.8rem", boxShadow:`0 4px 24px rgba(${P.rgb},0.35)`, fontFamily:P.fontBody }}>
            {loading ? "Salvando…" : "Salvar nova senha"}
          </button>
          {linkDead && (
            <button onClick={() => window.location.href = window.location.origin}
              style={{ width:"100%", background:"transparent", color:P.ac, border:`1px solid rgba(${P.rgb},0.35)`, borderRadius:11, padding:"0.9rem", fontWeight:700, fontSize:"0.9rem", cursor:"pointer", fontFamily:P.fontBody }}>
              ← Voltar para login
            </button>
          )}
        </div>
      </div>
    </>
  );
}
